import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import client from './client'
import { useAuthStore } from '../store/authStore'

export function useMangaList(params = {}) {
  return useQuery({
    queryKey: ['manga-list', params],
    queryFn: () => client.get('/api/manga', { params }).then((r) => r.data),
  })
}

export function useMangaListInfinite(params = {}) {
  return useInfiniteQuery({
    queryKey: ['manga-list-infinite', params],
    queryFn: ({ pageParam }) =>
      client.get('/api/manga', { params: { ...params, page: pageParam, limit: 24 } }).then((r) => r.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
  })
}

export function useManga(mangaId) {
  return useQuery({
    queryKey: ['manga', mangaId],
    queryFn: () => client.get(`/api/manga/${mangaId}`).then((r) => r.data),
    enabled: !!mangaId,
    retry: false,
  })
}

export function useUserRating(slug) {
  const isAuth = useAuthStore((s) => s.isAuth)
  return useQuery({
    queryKey: ['user-rating', slug],
    queryFn: () => client.get(`/api/manga/${slug}/rating`).then((r) => r.data),
    enabled: !!(isAuth && slug),
    retry: false,
  })
}

export function useRateManga(slug) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (score) =>
      client.post(`/api/manga/${slug}/rating`, { score }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manga', slug] })
      qc.invalidateQueries({ queryKey: ['user-rating', slug] })
    },
  })
}

export function useBookmark(mangaId) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['manga', mangaId] })
    qc.invalidateQueries({ queryKey: ['bookmarks'] })
  }
  const add = useMutation({
    mutationFn: (status = 'reading') =>
      client.post('/api/user/bookmarks', { manga_id: mangaId, status }).then((r) => r.data),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: (status) =>
      client.patch(`/api/user/bookmarks/${mangaId}`, { status }).then((r) => r.data),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: () =>
      client.delete(`/api/user/bookmarks/${mangaId}`).then((r) => r.data),
    onSuccess: invalidate,
  })
  return { add, update, remove }
}

export function useBookmarks() {
  const isAuth = useAuthStore((s) => s.isAuth)
  return useQuery({
    queryKey: ['bookmarks'],
    queryFn: () => client.get('/api/user/bookmarks').then((r) => r.data),
    enabled: isAuth,
  })
}

export function useMeta() {
  return useQuery({
    queryKey: ['meta'],
    queryFn: () => client.get('/api/meta').then((r) => r.data),
    staleTime: Infinity,
  })
}

export function useCreateGenre() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name) => client.post('/api/admin/genres', { name }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  })
}

export function useHistory() {
  const isAuth = useAuthStore((s) => s.isAuth)
  return useQuery({
    queryKey: ['history'],
    queryFn: () => client.get('/api/user/history').then((r) => r.data),
    enabled: isAuth,
  })
}

export function useSimilarManga(genres = [], excludeSlug = '') {
  const topGenres = genres.slice(0, 2).map((g) => g.code ?? g).join(',')
  return useQuery({
    queryKey: ['similar-manga', topGenres, excludeSlug],
    queryFn: () =>
      client.get('/api/manga', { params: { genre: topGenres, sort: 'rating', limit: 7 } })
        .then((r) => (r.data?.items || r.data || []).filter((m) => m.slug !== excludeSlug).slice(0, 6)),
    enabled: genres.length > 0 && !!excludeSlug,
  })
}

export function useSearch(query) {
  const showAdult = useAuthStore((s) => s.user?.show_adult ?? false)
  return useQuery({
    queryKey: ['search', query, showAdult],
    queryFn: () => client.get('/api/search', { params: { q: query, limit: 30, show_adult: showAdult } }).then((r) => r.data),
    enabled: !!query,
  })
}

export function useChapterCompletions(mangaId) {
  const isAuth = useAuthStore((s) => s.isAuth)
  return useQuery({
    queryKey: ['chapter-completions', mangaId],
    queryFn: () => client.get('/api/user/chapters/completed', { params: { manga_id: mangaId } }).then((r) => r.data),
    enabled: isAuth && !!mangaId,
  })
}

export function useCompletedChaptersCount() {
  const isAuth = useAuthStore((s) => s.isAuth)
  return useQuery({
    queryKey: ['chapter-completions-count'],
    queryFn: () => client.get('/api/user/chapters/completed/count').then((r) => r.data.count),
    enabled: isAuth,
  })
}

export function useMarkChapterComplete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (chapterId) => client.post('/api/user/chapters/complete', { chapter_id: chapterId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chapter-completions'] })
      qc.invalidateQueries({ queryKey: ['chapter-completions-count'] })
    },
  })
}

export function useResetMangaProgress(mangaId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => client.delete('/api/user/chapters/completed', { params: { manga_id: mangaId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chapter-completions', mangaId] })
      qc.invalidateQueries({ queryKey: ['chapter-completions-count'] })
    },
  })
}
